import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../../Component/Layout';
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import Select from "react-select";
import {ORDER_API, MASTER_API} from "../../apiConfig"
import { useLocation, useNavigate } from "react-router-dom";

// --- Shared dropdown option/menu styling (matches Sales Order dropdown look) ---
const dropdownOptionStyle = {
    control: (base) => ({
        ...base,
        minHeight: 32,
        height: 32,
        fontSize: '13px',
        borderColor: '#dee2e6',
        boxShadow: 'none',
        '&:hover': { borderColor: '#b3b3b3' }
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px', height: '32px', display: 'flex', alignItems: 'center' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    menu: (base) => ({ ...base, fontSize: '13px', zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#1e293b' : state.isFocused ? '#f1f5f9' : 'white',
        color: state.isSelected ? 'white' : '#334155',
        cursor: 'pointer',
        fontSize: '13px'
    })
};

function Billing() {
    const location = useLocation();
    const navigate = useNavigate();
    const [salesOrganization, setSalesOrganization] = useState("");
    const [customerSegment, setCustomerSegment] = useState("");
    const [dChannel, setDChannel] = useState("");
    const [odId, setOdId] = useState("");
    const [selectedCustomer, setSelectedCustomer] = useState("");
    const [rfqSummary, setRfqSummary] = useState([]);
    const [postedSummary, setPostedSummary] = useState([]);
    const [mainTab, setMainTab] = useState("active"); // "active" | "posted"
    const [selectedRows, setSelectedRows] = useState([]);
    const [modalData, setModalData] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('');
    const [viewSource, setViewSource] = useState(null);
    const [activeTab, setActiveTab] = useState("product");
    const [showProductModal, setShowProductModal] = useState(false);
    const [productDetails, setProductDetails] = useState(null);
    const [expandedProductOdId, setExpandedProductOdId] = useState(null);
    const [expandedDocsOdId, setExpandedDocsOdId] = useState(null);
    const [showPaymentTermsModal, setShowPaymentTermsModal] = useState(false);
    // --- MASTER DATA (from configuration, not derived from loaded rows) ---
    const [salesOrgList, setSalesOrgList] = useState([]);
    const [customerSegmentList, setCustomerSegmentList] = useState([]);
    const [channelList, setChannelList] = useState([]);
    const [customerList, setCustomerList] = useState([]);

    useEffect(() => {
        const fetchMasters = async () => {
            try {
                const [orgRes, segRes, chnRes, custRes] = await Promise.all([
                    fetch(`${MASTER_API}/configuration/sales-organization`),
                    fetch(`${MASTER_API}/configuration/dynamic/customer-segment`),
                    fetch(`${MASTER_API}/configuration/distribution-channel`),
                    fetch(`${ORDER_API}/billing/customers`)
                ]);
                const [org, seg, chn, cust] = await Promise.all([
                    orgRes.json(), segRes.json(), chnRes.json(), custRes.json()
                ]);
                setSalesOrgList(org.data || org || []);
                setCustomerSegmentList(seg.data || seg || []);
                setChannelList(chn.data || chn || []);
                setCustomerList(cust.data || cust || []);
            } catch (err) {
                console.error('Failed to load Billing master data:', err);
            }
        };
        fetchMasters();
    }, []);

    useEffect(() => {
        const fetchSummary = async () => {
            try {
                const res = await fetch(`${ORDER_API}/billing/summary/created`);
                const data = await res.json();
                setRfqSummary(data);
            } catch (err) {
                console.error('Failed to load Billing summary:', err);
            }
        };
        fetchSummary();
    }, []);

    useEffect(() => {
        if (mainTab !== "posted") return;
        const fetchPostedSummary = async () => {
            try {
                const res = await fetch(`${ORDER_API}/billing/summary/review`);
                const data = await res.json();
                setPostedSummary(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Failed to load Posted billing summary:', err);
            }
        };
        fetchPostedSummary();
    }, [mainTab]);

    const dropdownOptions = useMemo(() => {
        const getUnique = (key) => [...new Set(rfqSummary.map(item => item[key]).filter(Boolean))];
        return {
            // OD ID still comes from the loaded rows themselves (not a master table)
            odIds: getUnique('od_id')
        };
    }, [rfqSummary]);

    const filteredData = useMemo(() => {
        return rfqSummary.filter((item) => {
            return (
                (salesOrganization === "" || item.sales_organization === salesOrganization) &&
                (customerSegment === "" || item.division === customerSegment) &&
                (dChannel === "" || item.distribution_channel === dChannel) &&
                (selectedCustomer === "" || item.customer === selectedCustomer) &&
                (odId === "" || item.od_id.includes(odId))
            );
        });
    }, [rfqSummary, salesOrganization, customerSegment, dChannel, selectedCustomer, odId]);

    const filteredPostedData = useMemo(() => {
        return postedSummary.filter((item) => {
            return (
                (salesOrganization === "" || item.sales_organization === salesOrganization) &&
                (customerSegment === "" || item.division === customerSegment) &&
                (dChannel === "" || item.distribution_channel === dChannel) &&
                (selectedCustomer === "" || item.customer === selectedCustomer)
            );
        });
    }, [postedSummary, salesOrganization, customerSegment, dChannel, selectedCustomer]);

    useEffect(() => {
        if (location.state?.openBiId) {
            handlePostedViewClick(location.state.openBiId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state?.openBiId]);

    useEffect(() => {
        if (showModal) {
            setActiveTab("product");
        }
    }, [showModal]);

    const handleToggle = (tab, id) => {
        if (tab === "product") setExpandedProductOdId(prev => prev === id ? null : id);
        if (tab === "docs") setExpandedDocsOdId(prev => prev === id ? null : id);
    };

    useEffect(() => {
        if (showModal) {
            setActiveTab("product");
            setExpandedProductOdId(null);
            setExpandedDocsOdId(null);
        }
    }, [showModal]);

    const renderProductContent = (item) => {
        const totals = item.productDetails?.reduce((acc, curr) => {
            const igst = parseFloat(curr.igst_amount) || 0;
            const rawTotalGst = parseFloat(curr.total_gst) || 0;
            const rawGrandTotal = parseFloat(curr.final_price_after_gst) || 0;
            const grossRow = (parseFloat(curr.unit_price) || 0) * (parseFloat(curr.delivery_qty) || 0);

            return {
                totalValue: acc.totalValue + grossRow,
                netPrice: acc.netPrice + (parseFloat(curr.net_price_after_discount) || 0),
                cgst: acc.cgst + (parseFloat(curr.cgst_amount) || 0),
                sgst: acc.sgst + (parseFloat(curr.sgst_amount) || 0),
                igst: acc.igst + igst,
                totalGst: acc.totalGst + rawTotalGst,
                grandTotal: acc.grandTotal + rawGrandTotal
            };
        }, { totalValue: 0, netPrice: 0, cgst: 0, sgst: 0, igst: 0, totalGst: 0, grandTotal: 0 });

        return (
            <div style={{ ...styles.tableContainer, overflowX: 'auto', marginTop: '0', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <table className="table table-hover mb-0" style={{ ...styles.erpTable, width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ backgroundColor: '#f8fafc' }}>
                            <th style={{ ...styles.erpTh, textAlign: "center", width: '50px' }}>Item</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>Product Code</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>Description</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>UOM</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>Unit Price</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>Qty</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap' }}>Del. Qty</th>

                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap', textAlign: 'right' }}>Total Value</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap', textAlign: 'right' }}>Disc %</th>

                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap', textAlign: 'right' }}>Net Price</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap', textAlign: 'right' }}>CGST</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap', textAlign: 'right' }}>SGST</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap', textAlign: 'right' }}>IGST</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap', textAlign: 'right' }}>Total GST</th>
                            <th style={{ ...styles.erpTh, whiteSpace: 'nowrap', textAlign: 'right' }}>Grand Total</th>

                            <th style={styles.erpTh}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {item.productDetails?.length === 0 ? (
                            <tr><td colSpan={16} style={{ ...styles.erpTd, textAlign: "center", padding: '20px' }}>No Products Found</td></tr>
                        ) : (
                            <>
                                {item.productDetails?.map((prod, idx) => {
                                    const grossRow = (parseFloat(prod.unit_price) || 0) * (parseFloat(prod.delivery_qty) || 0);

                                    const igstRow = parseFloat(prod.igst_amount) || 0;
                                    const totalGstRow = parseFloat(prod.total_gst) || 0;
                                    const grandTotalRow = parseFloat(prod.final_price_after_gst) || 0;

                                    return (
                                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ ...styles.erpTd, textAlign: "center" }}>{idx + 1}</td>
                                            <td style={styles.erpTd}>{prod.product_code}</td>
                                            <td style={styles.erpTd}>{prod.description}</td>
                                            <td style={styles.erpTd}>{prod.uom}</td>
                                            <td style={styles.erpTd}>{prod.unit_price}</td>
                                            <td style={styles.erpTd}>{prod.quantity}</td>
                                            <td style={styles.erpTd}>{prod.delivery_qty}</td>

                                            {/* --- NEW DATA CELLS --- */}
                                            <td style={{ ...styles.erpTd, textAlign: 'right', fontWeight: '500' }}>{grossRow.toFixed(2)}</td>
                                            <td style={{ ...styles.erpTd, textAlign: 'right' }}>{prod.discount || 0}</td>

                                            {/* --- FINANCIAL CELLS --- */}
                                            <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(parseFloat(prod.net_price_after_discount || 0).toFixed(2))}</td>
                                            <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(parseFloat(prod.cgst_amount || 0).toFixed(2))}</td>
                                            <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(parseFloat(prod.sgst_amount || 0).toFixed(2))}</td>
                                            <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(igstRow.toFixed(2))}</td>
                                            <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totalGstRow.toFixed(2))}</td>
                                            <td style={{ ...styles.erpTd, textAlign: 'right', fontWeight: '600' }}>{formatCurrency(grandTotalRow.toFixed(2))}</td>

                                            <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                                <button
                                                    className="btn btn-sm btn-outline-dark"
                                                    style={{ fontSize: '11px', padding: '2px 10px' }}
                                                    onClick={() => handleViewProductDetails(prod.id)}
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}

                                {/* --- SUMMARY ROW --- */}
                                <tr style={{ backgroundColor: '#f1f5f9', borderTop: '2px solid #e2e8f0', fontWeight: '700' }}>
                                    <td colSpan={7} style={{ ...styles.erpTd, textAlign: 'right', color: '#64748b' }}>TOTAL:</td>
                                    <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.totalValue.toFixed(2))}</td>
                                    <td style={{ ...styles.erpTd, textAlign: 'right' }}>-</td>
                                    <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.netPrice.toFixed(2))}</td>
                                    <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.cgst.toFixed(2))}</td>
                                    <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.sgst.toFixed(2))}</td>
                                    <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.igst.toFixed(2))}</td>
                                    <td style={{ ...styles.erpTd, textAlign: 'right' }}>{formatCurrency(totals.totalGst.toFixed(2))}</td>
                                    <td style={{ ...styles.erpTd, textAlign: 'right', color: '#0f172a', fontSize: '13px' }}>{formatCurrency(totals.grandTotal.toFixed(2))}</td>
                                    <td></td>
                                </tr>
                            </>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderDocsContent = (item) => (
        <div className="list-group mt-2">
            {item.documents?.map((file, idx) => (
                <div className="list-group-item d-flex justify-content-between align-items-center" key={idx}>
                    <span>{file.file_name}</span>
                    <a href={file.file_url.startsWith('http') ? file.file_url : ``} download className="btn btn-outline-dark btn-sm"><i className="bi bi-download"></i></a>
                </div>
            ))}
            {(!item.documents || item.documents.length === 0) && (
                <div className="p-4 text-center text-muted border rounded bg-light">No documents available.</div>
            )}
        </div>
    );

    const handleViewClick = async (od_id) => {
        try {
            const res = await fetch(`${ORDER_API}/billing/accepted/od/${od_id}`);
            const data = await res.json();
            setModalData([data]);
            setModalType('view');
            setViewSource('active');
            setShowModal(true);
        } catch (error) { console.error("Error fetching details:", error); }
    };

    const handlePostedViewClick = async (bi_id) => {
        try {
            const res = await fetch(`${ORDER_API}/billing/submitted/bi/${bi_id}`);
            const data = await res.json();
            setModalData([data]);
            setModalType('view');
            setViewSource('posted');
            setShowModal(true);
        } catch (error) { console.error("Error fetching posted billing details:", error); }
    };

    const handleOpenModal = async () => {
        const firstSelectedCustomer = rfqSummary.find(item => selectedRows.includes(item.od_id))?.customer;
        const allSameCustomer = selectedRows.every(odId => rfqSummary.find(item => item.od_id === odId)?.customer === firstSelectedCustomer);

        if (!allSameCustomer) { alert("Please select OD IDs of the same customer."); return; }

        const selectedData = rfqSummary.filter(item => selectedRows.includes(item.od_id));
        const od_ids = selectedData.map(item => item.od_id);

        if (od_ids.length > 0) {
            try {
                const responses = await Promise.all(od_ids.map(odId =>
                    fetch(`${ORDER_API}/billing/accepted/od/${odId}`).then(res => res.json())
                ));
                setModalData(responses);
                setModalType('convert');
                setShowModal(true);
            } catch (error) { console.error(error); alert("Error fetching data"); }
        } else { alert("No SO ID selected!"); }
    };

    const handleViewProductDetails = async (id) => {
        try {
            const response = await fetch(`${ORDER_API}/billing/item/${id}`);
            if (response.ok) {
                const data = await response.json();
                setProductDetails(data);
                setShowProductModal(true);
            } else {
                alert("Failed to fetch product details.");
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleCheckboxChange = (odId) => {
        setSelectedRows((prev) => {
            const isSelected = prev.includes(odId);
            if (isSelected) return prev.filter(id => id !== odId);
            const selectedCust = rfqSummary.find(item => item.od_id === odId)?.customer;
            const firstCust = prev.length > 0 ? rfqSummary.find(item => item.od_id === prev[0])?.customer : null;
            if (firstCust && selectedCust !== firstCust) {
                alert("Please select SO IDs of the same customer.");
                return prev;
            }
            return [...prev, odId];
        });
    };

    const handleSelectAllChange = (checked) => {
        if (checked) {
            if (filteredData.length === 0) return;
            const firstCustomer = filteredData[0]?.customer;
            if (!filteredData.every(item => item.customer === firstCustomer)) {
                alert("Items belong to different customers. Select manually.");
                return;
            }
            setSelectedRows(filteredData.map(item => item.od_id));
        } else {
            setSelectedRows([]);
        }
    };

    const handleCreateBilling = () => {
        const now = new Date();
        const pad = (n) => n.toString().padStart(2, "0");
        return `BI${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}`;
    };

    const handleSaveBilling = async () => {
        const generatedBiId = handleCreateBilling();
        const allProducts = modalData.flatMap(data => data.productDetails);
        const allOdIds = modalData.map(data => data.od_id);
        const primaryData = modalData[0];

        const formData = {
            od_id: allOdIds,
            productDetails: allProducts.map(p => ({ ...p, id: p.id })),
            customer_name: primaryData.customer,
            salesOrganization: primaryData.salesOrganization,
            division: primaryData.division, // Customer Segment (DB column: division)
            distributionChannel: primaryData.distributionChannel,
            gstin: primaryData.gstin,
            shipToAddress: primaryData.shipToAddress,
            billToAddress: primaryData.billToAddress,
            paymentTerms: primaryData.paymentTerms,
            accountNumber: primaryData.accountNumber,
            ifscCode: primaryData.ifscCode,
            bi_id: generatedBiId,
        };

        try {
            const response = await fetch(`${ORDER_API}/billing/create-billing`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });
            const data = await response.json();

            if (response.ok) {
                toast.success(`Billing ID ${generatedBiId} created successfully`);
                setShowModal(false);
                window.location.reload();
            } else {
                toast.error(`Error: ${data.message}`);
            }
        } catch (error) {
            console.error(error);
            toast.error("An error occurred.");
        }
    };

    const handleOdIdClick = (od_id) => {
        navigate("/cable/outbound-process/outbound-delivery", {
            state: {
                openOdId: od_id,
                returnTo: location.pathname
            }
        });
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedRows([]);
        setViewSource(null);

        if (location.state?.returnTo) {
            navigate(location.state.returnTo, {
                state: { returnParams: location.state.returnParams }
            });
        }
    };

    const InfoField = ({ label, value }) => (
        <div style={{ marginBottom: '10px' }}>
            <label style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: '700',
                color: '#64748b',
                textTransform: 'uppercase',
                marginBottom: '2px'
            }}>
                {label}
            </label>
            <div style={{
                fontSize: '13px',
                fontWeight: '500',
                color: '#1e293b',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
            }}>
                {value || "-"}
            </div>
        </div>
    );

    const formatCurrency = (value) => {
        const val = Number(value);
        if (isNaN(val)) return "0";
        if (val >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
        if (val >= 100000) return `${(val / 100000).toFixed(2)} Lakh`;
        return val.toLocaleString('en-IN');
    };

    return (
        <Layout>
            <div style={styles.pageTitleBar}>
                <div>
                    <div style={styles.pageTitle}>Billing</div>
                    <div style={styles.pageSubtitle}>Manage and view your data here</div>
                </div>
                <button className="btn btn-dark" style={styles.primaryBtn} onClick={handleOpenModal} disabled={mainTab === "posted"}>
                    + Create Bill
                </button>
            </div>

            <div style={styles.filterCard}>
                <div className="row g-3">
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>Sales Organization</label>
                        <Select
                            options={salesOrgList.map(item => ({ value: item.code || item.name || item.sales_organization, label: item.code || item.name || item.sales_organization }))}
                            value={salesOrganization ? { value: salesOrganization, label: salesOrganization } : null}
                            onChange={opt => setSalesOrganization(opt ? opt.value : "")}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>Customer Segment</label>
                        <Select
                            options={customerSegmentList.map(item => ({ value: item.name || item.code || item.customer_segment || item, label: item.name || item.code || item.customer_segment || item }))}
                            value={customerSegment ? { value: customerSegment, label: customerSegment } : null}
                            onChange={opt => setCustomerSegment(opt ? opt.value : "")}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>Distribution Channel</label>
                        <Select
                            options={channelList.map(item => ({ value: item.code || item.name || item.distribution_channel, label: item.code || item.name || item.distribution_channel }))}
                            value={dChannel ? { value: dChannel, label: dChannel } : null}
                            onChange={opt => setDChannel(opt ? opt.value : "")}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-2 col-sm-4">
                        <label style={styles.filterLabel}>OD ID</label>
                        <Select
                            options={dropdownOptions.odIds.map(opt => ({ value: opt, label: opt }))}
                            value={odId ? { value: odId, label: odId } : null}
                            onChange={opt => setOdId(opt ? opt.value : "")}
                            isClearable
                            placeholder="Select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                    <div className="col-md-4 col-sm-12">
                        <label style={styles.filterLabel}>Customer</label>
                        <Select
                            options={customerList.map(c => ({
                                value: `${c.partner_code} - ${c.business_partner_name}`,
                                label: `${c.partner_code} - ${c.business_partner_name}`
                            }))}
                            value={selectedCustomer ? { value: selectedCustomer, label: selectedCustomer } : null}
                            onChange={opt => setSelectedCustomer(opt ? opt.value : "")}
                            isClearable
                            placeholder="Select Customer"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={dropdownOptionStyle}
                        />
                    </div>
                </div>
            </div>

            {/* ===================== ACTIVE / POSTED TOGGLE ===================== */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "14px" }}>
                <div style={{ display: "inline-flex", background: "#f1f5f9", borderRadius: "8px", padding: "3px", gap: "2px" }}>
                    <button
                        onClick={() => setMainTab("active")}
                        style={{
                            border: "none", borderRadius: "6px", padding: "6px 18px", fontSize: "13px", fontWeight: 600, cursor: "pointer",
                            background: mainTab === "active" ? "#ffffff" : "transparent",
                            color: mainTab === "active" ? "#1e293b" : "#64748b",
                            boxShadow: mainTab === "active" ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                            transition: "all 0.15s ease"
                        }}
                    >
                        Active
                    </button>
                    <button
                        onClick={() => setMainTab("posted")}
                        style={{
                            border: "none", borderRadius: "6px", padding: "6px 18px", fontSize: "13px", fontWeight: 600, cursor: "pointer",
                            background: mainTab === "posted" ? "#ffffff" : "transparent",
                            color: mainTab === "posted" ? "#1e293b" : "#64748b",
                            boxShadow: mainTab === "posted" ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                            transition: "all 0.15s ease"
                        }}
                    >
                        Posted
                    </button>
                </div>
            </div>

            {mainTab === "posted" ? (
            <div style={styles.tableContainer}>
                <table className="table" style={styles.erpTable}>
                    <thead>
                        <tr>
                            <th style={{ ...styles.erpTh, width: 150 }}>Billing ID</th>
                            <th style={{ ...styles.erpTh, width: 150 }}>Customer</th>
                            <th style={{ ...styles.erpTh, width: 150 }}>Billing Date</th>
                            <th style={{ ...styles.erpTh, width: 150 }}>Total Price</th>
                            <th style={{ ...styles.erpTh, width: 150 }}>Status</th>
                            <th style={{ ...styles.erpTh, width: 100 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredPostedData.length === 0 ? (
                            <tr><td colSpan="6" style={{ ...styles.erpTh, textAlign: "center" }}>No Records Found</td></tr>
                        ) : (
                            filteredPostedData.map((item) => (
                                <tr key={item.bi_id}>
                                    <td style={styles.erpTd}>{item.bi_id ? (
                                        <span
                                            onClick={() => handlePostedViewClick(item.bi_id)}
                                            style={{ color: "#1976d2", cursor: "pointer", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: "2px" }}
                                        >
                                            {item.bi_id}
                                        </span>
                                    ) : ""}</td>
                                    <td style={styles.erpTd}>{item.customer || ""}</td>
                                    <td style={styles.erpTd}>
                                        {item.bi_date ? (() => {
                                            const d = new Date(item.bi_date);
                                            const day = String(d.getDate()).padStart(2, '0');
                                            const month = String(d.getMonth() + 1).padStart(2, '0');
                                            const year = d.getFullYear();
                                            return `${day}/${month}/${year}`;
                                        })() : ""}
                                    </td>
                                    <td style={styles.erpTd}>{item.total_price}</td>
                                    <td style={styles.erpTd}>{item.status}</td>
                                    <td style={styles.erpTd}>
                                        <button className="btn btn-outline-dark btn-sm" onClick={() => handlePostedViewClick(item.bi_id)}>View</button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            ) : (
            <div style={styles.tableContainer}>
                <table className="table" style={styles.erpTable}>
                    <thead>
                        <tr>
                            <th style={{ ...styles.erpTh, width: 50, textAlign: "center" }}>
                                <input type="checkbox"
                                    checked={filteredData.length > 0 && selectedRows.length > 0 && filteredData.every(item => selectedRows.includes(item.od_id))}
                                    onChange={(e) => handleSelectAllChange(e.target.checked)} />
                            </th>
                            <th style={{ ...styles.erpTh, width: 150 }}>OD ID</th>
                            <th style={{ ...styles.erpTh, width: 150 }}>Customer</th>
                            <th style={{ ...styles.erpTh, width: 150 }}>Requirement Date</th>
                            <th style={{ ...styles.erpTh, width: 150 }}>Total Price</th>
                            <th style={{ ...styles.erpTh, width: 150 }}>Status</th>
                            <th style={{ ...styles.erpTh, width: 100 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.length === 0 ? (
                            <tr><td colSpan="9" style={{ ...styles.erpTh, textAlign: "center" }}>No Records Found</td></tr>
                        ) : (
                            filteredData.map((item) => (
                                <tr key={item.so_id} style={{ backgroundColor: item.status === 'Submitted' ? "#f2f2f2" : "white", opacity: item.status === 'Submitted' ? 0.6 : 1 }}>
                                    <td style={{ ...styles.erpTh, width: 50, textAlign: "center" }}>
                                        <input type="checkbox" checked={selectedRows.includes(item.od_id)} onChange={() => handleCheckboxChange(item.od_id)} />
                                    </td>
                                    <td style={styles.erpTd}>{item.od_id ? (
                                        <span
                                            onClick={() => handleOdIdClick(item.od_id)}
                                            style={{ color: "#1976d2", cursor: "pointer", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: "2px" }}
                                        >
                                            {item.od_id}
                                        </span>
                                    ) : ""}</td>
                                    <td style={styles.erpTd}>{item.customer || ""}</td>
                                    <td style={styles.erpTd}>
                                        {item.requirement_date ? (() => {
                                            const d = new Date(item.requirement_date);
                                            const day = String(d.getDate()).padStart(2, '0');
                                            const month = String(d.getMonth() + 1).padStart(2, '0');
                                            const year = d.getFullYear();
                                            return `${day}/${month}/${year}`;
                                        })() : "N/A"}
                                    </td>
                                    {/* <td style={styles.erpTd}>{item.requirement_date ? new Date(item.requirement_date).toLocaleDateString("en-GB") : "N/A"}</td> */}
                                    <td style={styles.erpTd}>{item.total_price}</td>
                                    <td style={styles.erpTd}>{item.status}</td>
                                    <td style={styles.erpTd}>
                                        <button className="btn btn-outline-dark btn-sm" onClick={() => handleViewClick(item.od_id)}>View</button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            )}

            {/* --- IMPROVED MODAL UI --- */}
            {showModal && modalData.length > 0 && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, maxWidth: "1200px", width: "95%", maxHeight: "95vh" }}>
                        <div style={styles.modalHeader}>
                            <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#1e293b' }}>
                                {modalType === 'convert' ? 'Create New Bill' : viewSource === 'posted' ? (
                                    `View Bill: ${modalData[0]?.bi_id || ""}`
                                ) : (
                                    <>
                                        View Bill:{" "}
                                        <span
                                            onClick={() => modalData[0]?.od_id && handleOdIdClick(modalData[0].od_id)}
                                            style={{ color: "#1976d2", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: "2px" }}
                                        >
                                            {modalData[0]?.od_id || ""}
                                        </span>
                                    </>
                                )}
                            </div>
                            <button style={styles.iconBtn} onClick={handleCloseModal}>✕</button>
                        </div>

                        <div style={styles.modalBody}>
                            {/* --- HEADER SUMMARY SECTION --- */}
                            <div className="p-3 mb-3" style={{ backgroundColor: '#f1f5f9', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <div className="row">
                                    <div className="col-md-4">
                                        <InfoField label="Customer" value={modalData[0]?.customer} />
                                    </div>
                                    <div className="col-md-3">
                                        <InfoField label="Sales Org" value={modalData[0]?.salesOrganization} />
                                    </div>
                                    <div className="col-md-3">
                                        <InfoField label="Customer Segment" value={modalData[0]?.division} />
                                    </div>
                                    <div className="col-md-2">
                                        <InfoField label="Dist. Channel" value={modalData[0]?.distributionChannel} />
                                    </div>
                                </div>
                            </div>

                            {/* --- DETAILS GRID --- */}
                            <div className="row g-3 mb-4">
                                {/* Addresses */}
                                <div className="col-md-8">
                                    <div className="p-3 h-100" style={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                                        {/* <h6 style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', color: '#475569', marginBottom: '10px' }}></h6> */}
                                        <div className="row">
                                            <div className="col-6 border-end">
                                                <label className="text-muted fw-bold" style={{ fontSize: '11px' }}>SHIPPING ADDRESS</label>
                                                <div style={{ fontSize: '13px', color: '#334155', whiteSpace: 'pre-wrap' }}>{modalData[0]?.shipToAddress || ""}</div>
                                            </div>
                                            <div className="col-6 ps-3">
                                                <label className="text-muted fw-bold" style={{ fontSize: '11px' }}>BILLING ADDRESS</label>
                                                <div style={{ fontSize: '13px', color: '#334155', whiteSpace: 'pre-wrap' }}>{modalData[0]?.billToAddress || ""}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Financials */}
                                <div className="col-md-4">
                                    <div className="p-3 h-100" style={{ border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#fff' }}>
                                        {/* <h6 style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', color: '#475569', marginBottom: '10px' }}></h6> */}
                                        <div className="d-flex justify-content-between mb-2">
                                            <span style={{ fontSize: '12px', color: '#64748b' }}>GSTIN</span>
                                            <span style={{ fontSize: '13px', fontWeight: '500' }}>{modalData[0]?.gstin || ""}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2">
                                            <span style={{ fontSize: '12px', color: '#64748b' }}>Account No</span>
                                            <span style={{ fontSize: '13px', fontWeight: '500' }}>{modalData[0]?.accountNumber || ""}</span>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span style={{ fontSize: '12px', color: '#64748b' }}>IFSC Code</span>
                                            <span style={{ fontSize: '13px', fontWeight: '500' }}>{modalData[0]?.ifscCode || ""}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* --- PAYMENT TERMS (own row, Sales-Order style) --- */}
                            <div className="mb-4">
                                <label style={styles.compactLabel}>Payment Terms</label>
                                <div className="d-flex gap-2">
                                    <div
                                        className="form-control bg-light"
                                        style={{ ...styles.compactInput, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    >
                                        {modalData[0]?.paymentTerms || ""}
                                    </div>
                                    <button
                                        className="btn btn-dark btn-sm"
                                        onClick={() => setShowPaymentTermsModal(true)}
                                    >
                                        View
                                    </button>
                                </div>
                            </div>

                            {/* --- TABS --- */}
                            <div className="d-flex border-bottom mb-3">
                                <button
                                    className={`btn btn-sm pb-2 rounded-0 ${activeTab === 'product' ? 'border-bottom border-dark fw-bold text-dark' : 'text-muted'}`}
                                    style={{ border: 'none', background: 'none', paddingLeft: '0', marginRight: '20px' }}
                                    onClick={() => setActiveTab('product')}
                                >
                                    Product
                                </button>
                                <button
                                    className={`btn btn-sm pb-2 rounded-0 ${activeTab === 'docs' ? 'border-bottom border-dark fw-bold text-dark' : 'text-muted'}`}
                                    style={{ border: 'none', background: 'none' }}
                                    onClick={() => setActiveTab('docs')}
                                >
                                    Document
                                </button>
                            </div>

                            {/* --- TAB CONTENT --- */}
                            <div>
                                {modalData.map((item) => (
                                    <div key={item.od_id} className="mb-3">
                                        {/* --- TAB CONTENT --- */}

                                        {activeTab === "product" && (
                                            <div>
                                                {modalData.map((item) => (
                                                    <div key={item.od_id} style={{ marginBottom: "15px" }}>
                                                        {/* VIEW MODE: Always expanded (Simple Card) */}
                                                        {modalType === 'view' ? (
                                                            <div style={{ padding: "10px", border: "1px solid #ddd", borderRadius: "8px", backgroundColor: "#fff" }}>
                                                                {renderProductContent(item)}
                                                            </div>
                                                        ) : (
                                                            // CREATE MODE: Accordion Style
                                                            <div style={{ padding: "10px", border: "1px solid #ddd", borderRadius: "8px", backgroundColor: "#f9f9f9" }}>
                                                                <div
                                                                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
                                                                    onClick={() => handleToggle("product", item.od_id)}
                                                                >
                                                                    <h6 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#334155' }}>
                                                                        {item.od_id}
                                                                    </h6>
                                                                    <i className={`bi bi-chevron-${expandedProductOdId === item.od_id ? "down" : "right"}`} style={{ fontSize: "18px" }}></i>
                                                                </div>
                                                                {expandedProductOdId === item.od_id && (
                                                                    <div style={{ marginTop: '10px' }}>
                                                                        {renderProductContent(item)}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {activeTab === "docs" && (
                                            <div>
                                                {modalData.map((item) => (
                                                    <div key={item.od_id} style={{ marginBottom: "15px" }}>
                                                        {modalType === 'view' ? (
                                                            <div style={{ padding: "10px", border: "1px solid #ddd", borderRadius: "8px", backgroundColor: "#fff" }}>
                                                                {renderDocsContent(item)}
                                                            </div>
                                                        ) : (
                                                            <div style={{ padding: "10px", border: "1px solid #ddd", borderRadius: "8px", backgroundColor: "#f9f9f9" }}>
                                                                <div
                                                                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
                                                                    onClick={() => handleToggle("docs", item.od_id)}
                                                                >
                                                                    <h6 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#334155' }}>
                                                                        {item.od_id}
                                                                    </h6>
                                                                    <i className={`bi bi-chevron-${expandedDocsOdId === item.od_id ? "down" : "right"}`} style={{ fontSize: "18px" }}></i>
                                                                </div>
                                                                {expandedDocsOdId === item.od_id && (
                                                                    <div style={{ marginTop: '10px' }}>
                                                                        {renderDocsContent(item)}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div style={styles.modalFooter}>
                            <button className="btn btn-outline-secondary btn-sm" onClick={handleCloseModal}>Close</button>
                            <button className="btn btn-dark btn-sm" onClick={handleSaveBilling}>Save</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Payment Terms Detail Modal (Sales-Order style) */}
            {showPaymentTermsModal && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, maxWidth: "700px" }}>
                        <div style={styles.modalHeader}>
                            <strong>Payment Terms</strong>
                            <button style={styles.iconBtn} onClick={() => setShowPaymentTermsModal(false)}>✕</button>
                        </div>
                        <div style={styles.modalBody}>
                            <div style={{ fontSize: '13px', color: '#334155', whiteSpace: 'pre-wrap' }}>
                                {modalData[0]?.paymentTerms || ""}
                            </div>
                        </div>
                        <div style={styles.modalFooter}>
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => setShowPaymentTermsModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Product View Details Modal */}
            {showProductModal && productDetails && (
                <div style={styles.modalBackdrop}>
                    <div
                        style={{
                            ...styles.modal,
                            maxWidth: "950px",
                            width: "95%",
                            maxHeight: "85vh",
                            padding: "14px",
                            fontSize: "13px"
                        }}
                    >


                        {/* ================= BODY ================= */}
                        <div style={{ padding: "12px" }}>

                            {/* ===== TOP ROW (LIKE PIC) ===== */}
                            <table className="table table-bordered table-sm mb-3">
                                <thead style={{ background: "#f8fafc", fontWeight: 600 }}>
                                    <tr>
                                        <th>Product Code</th>
                                        <th>HSN Code</th>
                                        <th>Quantity</th>
                                        <th>Standard Price</th>
                                        <th>Discount (%)</th>
                                        <th>Total Price</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{productDetails.product_code}</td>
                                        <td>{productDetails.hsn_code}</td>
                                        <td>{productDetails.delivery_qty}</td>
                                        <td>₹ {productDetails.unit_price}</td>
                                        <td>{productDetails.discount}</td>
                                        <td style={{ fontWeight: 600 }}>
                                            ₹ {productDetails.totalPrice}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            {/* ===== NET PRICE LINE ===== */}
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    padding: "8px 10px",
                                    borderTop: "1px solid #999",
                                    borderBottom: "1px solid #999",
                                    marginBottom: "10px",
                                    fontWeight: 600
                                }}
                            >
                                <span>Net Price :</span>
                                <span>₹ {productDetails.netPrice}</span>
                            </div>

                            {/* ===== GST BREAKUP ===== */}
                            <table
                                className="table table-sm"
                                style={{ width: "50%", marginLeft: "auto" }}
                            >
                                <tbody>
                                    <tr>
                                        <td>CGST @ {productDetails.cgst}%</td>
                                        <td style={{ textAlign: "right" }}>
                                            ₹ {productDetails.cgstAmount}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>SGST @ {productDetails.sgst}%</td>
                                        <td style={{ textAlign: "right" }}>
                                            ₹ {productDetails.sgstAmount}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>IGST @ {productDetails.igst}%</td>
                                        <td style={{ textAlign: "right" }}>
                                            ₹ {productDetails.igstAmount}
                                        </td>
                                    </tr>
                                    <tr style={{ borderTop: "1px solid #000", fontWeight: 600 }}>
                                        <td>Total GST</td>
                                        <td style={{ textAlign: "right" }}>
                                            ₹ {productDetails.totalGST}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            {/* ===== GRAND TOTAL ===== */}
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    padding: "12px",
                                    marginTop: "8px",
                                    background: "#f1f5f9",
                                    fontWeight: 700,
                                    fontSize: "15px",
                                    borderRadius: "4px"
                                }}
                            >
                                <span>Grand Total :</span>
                                <span>₹ {productDetails.finalPriceWithGST}</span>
                            </div>
                        </div>

                        {/* ================= FOOTER ================= */}
                        <div style={{ ...styles.modalFooter, padding: "10px 14px" }}>
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => setShowProductModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={3000} />
        </Layout>
    );
}

const styles = {
    compactLabel: { fontSize: "12px", fontWeight: 600, marginBottom: "2px", color: '#334155' },
    compactInput: { fontSize: "13px", height: "34px", padding: "4px 8px" },
    compactTabBtn: { fontSize: "13px", padding: "4px 10px" },
    tableContainer: { background: "#ffffff", borderRadius: 8, padding: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
    erpTable: { width: "100%", tableLayout: "auto", borderCollapse: "collapse" },
    erpTh: { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "10px 12px", borderBottom: "1px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap" },
    erpTd: { fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle" },
    filterCard: { background: '#ffffff', borderRadius: 8, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 18 },
    filterLabel: { fontSize: 12, fontWeight: 600, marginBottom: 6, color: '#334155' },
    filterSelect: { height: 36, fontSize: 13 },
    pageTitleBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
    pageTitle: { fontSize: 20, fontWeight: 700, color: '#1e293b' },
    pageSubtitle: { fontSize: 12, color: '#64748b' },
    primaryBtn: { paddingLeft: 14, paddingRight: 14 },
    modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, padding: '20px' },
    modal: { background: '#fff', borderRadius: '8px', width: '90%', maxWidth: '1200px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    modalHeader: { padding: '15px 20px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    iconBtn: { background: 'transparent', border: 'none', fontSize: '16px', cursor: 'pointer' },
    modalBody: { padding: '20px', overflowY: 'auto', flex: 1 },
    modalFooter: { padding: '15px 20px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end', gap: '10px' },
    tabs: { display: 'flex', gap: 8, margin: '14px 0' },
};

export default Billing;